import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Home, BookOpen, Star, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import { trackEvent } from '../../lib/analytics';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, userProfile, signOut } = useAuth();
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/', icon: <Home size={18} /> },
    { name: 'Readings', path: '/readings', icon: <BookOpen size={18} /> },
    { name: 'Profile', path: '/profile', icon: <User size={18} /> },
  ];

  // Only show Premium link if user is not premium
  if (!userProfile?.is_premium) {
    navLinks.splice(2, 0, { name: 'Premium', path: '/premium', icon: <Star size={18} /> });
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    trackEvent('navigation', 'menu_toggle', isMenuOpen ? 'close' : 'open');
  };

  const handleSignOut = async () => {
    trackEvent('auth', 'sign_out', 'navbar');
    await signOut();
    setIsMenuOpen(false);
  };

  const handleNavLinkClick = (linkName: string, path: string) => {
    trackEvent('navigation', 'nav_link_click', linkName);
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-primary-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center"
              onClick={() => trackEvent('navigation', 'logo_click', 'home')}
            >
              <img 
                src="/AI_Pandit_Ji_Logo_100x100.png" 
                alt="AI Pandit Ji" 
                className="h-10 w-10 rounded-full"
              />
              <span className="ml-2 text-xl font-display font-semibold">AI Pandit Ji</span>
              {userProfile?.is_premium && (
                <span className="ml-2 text-xs bg-secondary-500 text-white px-2 py-0.5 rounded-full">
                  Premium
                </span>
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-4">
              {user && navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${
                    location.pathname === link.path
                      ? 'bg-primary-700 text-white'
                      : 'text-primary-100 hover:bg-primary-700'
                  }`}
                  onClick={() => handleNavLinkClick(link.name, link.path)}
                >
                  <span className="mr-1">{link.icon}</span>
                  {link.name}
                </Link>
              ))}
              {user && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-primary-100 hover:bg-primary-700"
                  onClick={handleSignOut}
                  leftIcon={<LogOut size={18} />}
                >
                  Sign Out
                </Button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-primary-100 hover:bg-primary-700 focus:outline-none"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-primary-700">
            {user && navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-2 rounded-md text-base font-medium flex items-center ${
                  location.pathname === link.path
                    ? 'bg-primary-800 text-white'
                    : 'text-primary-100 hover:bg-primary-800'
                }`}
                onClick={() => handleNavLinkClick(link.name, link.path)}
              >
                <span className="mr-2">{link.icon}</span>
                {link.name}
              </Link>
            ))}
            {user && (
              <button
                onClick={handleSignOut}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-primary-100 hover:bg-primary-800 flex items-center"
              >
                <LogOut size={18} className="mr-2" />
                Sign Out
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;